import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import moment from 'moment';
import React, { useState } from 'react';
import { classNames } from '../helpers/functions';

moment.updateLocale('en', {
	week: {
		dow: 1,
	},
});

var enumerateDaysBetweenDates = (startDate, endDate, events) => {
	let today = moment();
	let startWeek = moment(startDate).utc().startOf('week');
	let endWeek = moment(endDate).utc().endOf('week');

	var currDate = moment(startDate).utc(); //.startOf('day');

	var lastDate = moment(endDate).utc().endOf('day');
	var beginNextMonthDate = moment(endDate).utc().add(1, 'day').endOf('day');

	let datesBefore = [];
	let dates = [];
	let datesAfter = [];

	const eventDays = events?.map((e) =>
		moment(e.completedDate || e.scheduledDate || e.estimatedDate)
			.utc()
			.startOf('d')
			.format(),
	);

	while (!startWeek.isSame(currDate, 'day')) {
		datesBefore.push({ date: startWeek.toDate() });
		startWeek.add(1, 'days');
	}

	while (!currDate.isAfter(lastDate, 'day')) {
		dates.push({
			date: currDate.format(),
			isCurrentMonth: true,
			isToday: today.utc().isSame(currDate, 'day'),
			isActive: eventDays.includes(currDate?.format()),
		});
		currDate.add(1, 'days');
	}

	while (!beginNextMonthDate.isAfter(endWeek, 'day')) {
		datesAfter.push({ date: beginNextMonthDate.toDate() });
		beginNextMonthDate.add(1, 'days');
	}

	return [...datesBefore, ...dates, ...datesAfter];
};

const generateMonth = (current, events) => {
	const generatedMonth = {
		name: current.isSame(moment(), 'year')
			? current.format('MMMM')
			: current.format('MMMM YYYY'),
		days: [
			...enumerateDaysBetweenDates(
				current.utc().startOf('month').toDate(),
				current.utc().endOf('month').toDate(),
				events,
			),
		],
	};

	return generatedMonth;
};

export const Calendar = ({
	monthSteps = 0,
	showMonthBefore = false,
	showMonthAhead = false,
	onNext,
	onPrev,
	events = [],
	onClick,
}) => {
	const today = moment();
	const current = today.add(monthSteps, 'months');

	const numberColumns =
		1 + (showMonthBefore ? 1 : 0) + (showMonthAhead ? 1 : 0);

	const currentMonth = generateMonth(current, events);

	const months = [
		...(showMonthBefore
			? [generateMonth(moment().add(monthSteps - 1, 'months'), events)]
			: []),
		currentMonth,
		...(showMonthAhead
			? [generateMonth(moment().add(monthSteps + 1, 'months'), events)]
			: []),
	];

	return (
		<div
			className={`relative grid grid-cols-1 gap-x-14 sm:grid-cols-${numberColumns}`}
		>
			{typeof onPrev === 'function' && (
				<button
					type="button"
					className="absolute -top-1 -left-1.5 flex items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
					onClick={() => onPrev()}
				>
					<span className="sr-only">Previous month</span>
					<ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
				</button>
			)}

			{months.map((month, monthIdx) => (
				<section
					key={monthIdx}
					className={classNames(
						monthIdx === months.length - 1 && 'hidden md:block',
						'text-center',
					)}
				>
					<h2 className="text-sm font-semibold text-gray-900">{month.name}</h2>
					<div className="mt-6 grid grid-cols-7 text-xs leading-6 text-gray-500">
						<div>M</div>
						<div>T</div>
						<div>W</div>
						<div>T</div>
						<div>F</div>
						<div>S</div>
						<div>S</div>
					</div>
					<div className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200">
						{month.days.map((day, dayIdx) => (
							<button
								key={day.date}
								type="button"
								className={classNames(
									day.isCurrentMonth
										? day.isToday
											? 'bg-indigo-600 text-white'
											: 'bg-white text-gray-900'
										: 'bg-gray-50 text-gray-400',
									dayIdx === 0 && 'rounded-tl-lg',
									dayIdx === 6 && 'rounded-tr-lg',
									dayIdx === month.days.length - 7 && 'rounded-bl-lg',
									dayIdx === month.days.length - 1 && 'rounded-br-lg',
									'relative py-1.5 focus:z-10',
									!day.isToday && day.isCurrentMonth && 'hover:bg-gray-100',
									!day.isCurrentMonth && 'pointer-events-none',
									day.isActive
										? 'text-indigo-700 border border-indigo-500 bg-indigo-50 hover:bg-indigo-100'
										: 'pointer-events-none',
								)}
								onClick={
									typeof onClick === 'function'
										? () => {
												onClick(day?.date);
											}
										: () => {}
								}
							>
								<time
									dateTime={day.date}
									className={classNames(
										day.isToday && 'bg-indigo-600 font-semibold text-white',
										'mx-auto flex h-7 w-7 items-center justify-center rounded-full',
									)}
								>
									{moment(day.date).utc().format('D')}
								</time>
							</button>
						))}
					</div>
				</section>
			))}

			{typeof onNext === 'function' && (
				<button
					type="button"
					className="absolute -top-1 -right-1.5 flex items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
					onClick={() => onNext()}
				>
					<span className="sr-only">Next month</span>
					<ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
				</button>
			)}
		</div>
	);
	return (
		<div
			className={`relative grid grid-cols-1 gap-x-14 md:grid-cols-${cols}`}
		></div>
	);
};
